import { type LicenseClaims, licenseClaimsSchema } from "@coach/shared";
import { importSPKI, jwtVerify } from "jose";

/**
 * Verify a license JWT against the server's Ed25519 public key.
 *
 * - Public key is shipped with the build (PUBLIC_LICENSE_VERIFY_KEY env var,
 *   base64-PEM encoded) so the app NEVER needs to fetch keys at runtime.
 * - Verification is purely offline; we still trust `exp` / `graceUntil`
 *   from the token because the key is held secret server-side.
 */
export interface VerifyResult {
  ok: boolean;
  claims?: LicenseClaims;
  reason?:
    | "malformed"
    | "bad-signature"
    | "expired"
    | "grace-expired"
    | "not-yet-valid"
    | "wrong-issuer"
    | "schema";
}

export async function verifyLicenseToken(
  token: string,
  publicKeyPemBase64: string,
  opts: { now?: number } = {},
): Promise<VerifyResult> {
  const now = Math.floor((opts.now ?? Date.now()) / 1000);

  let pem: string;
  try {
    pem = decodeBase64Utf8(publicKeyPemBase64);
  } catch {
    return { ok: false, reason: "malformed" };
  }

  if (!pem.includes("BEGIN PUBLIC KEY")) {
    return { ok: false, reason: "malformed" };
  }

  const key = await importSPKI(pem, "EdDSA").catch(() => null);
  if (!key) return { ok: false, reason: "malformed" };

  let verified;
  try {
    // We do our own `exp` / `nbf` / `graceUntil` arithmetic below so jose only
    // needs to validate the signature and issuer here. A huge clockTolerance
    // disables jose's built-in timestamp checks without us having to monkey
    // with `currentDate`.
    verified = await jwtVerify(token, key, {
      issuer: "coach-organizer",
      clockTolerance: 60 * 60 * 24 * 365 * 100,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message.toLowerCase() : "";
    if (msg.includes("signature")) return { ok: false, reason: "bad-signature" };
    if (msg.includes("issuer")) return { ok: false, reason: "wrong-issuer" };
    if (msg.includes("expired")) return { ok: false, reason: "expired" };
    return { ok: false, reason: "bad-signature" };
  }

  const parsed = licenseClaimsSchema.safeParse(verified.payload);
  if (!parsed.success) return { ok: false, reason: "schema" };

  const claims = parsed.data;
  // Clock-skew tolerance: device clocks routinely trail the server by
  // seconds-to-minutes. Without this margin a freshly minted token (nbf=now
  // server-side) is rejected as "not-yet-valid" on any device whose clock is
  // even 1s behind — surfacing as a constant "Sitzung abgelaufen" loop.
  if (claims.nbf > now + CLOCK_SKEW_S) {
    return { ok: false, reason: "not-yet-valid", claims };
  }
  if (claims.exp < now && claims.graceUntil < now) {
    return { ok: false, reason: "grace-expired", claims };
  }

  return { ok: true, claims };
}

/** Allowed clock skew between device and server (seconds). */
const CLOCK_SKEW_S = 5 * 60;

/**
 * Portable base64 → utf8 decoder. Works in Node (Buffer), browsers (atob),
 * and Tauri WebView. The license verifier runs in all three.
 */
function decodeBase64Utf8(b64: string): string {
  const g = globalThis as {
    Buffer?: {
      from: (s: string, enc: string) => { toString: (e: string) => string };
    };
  };
  if (typeof g.Buffer !== "undefined") {
    return g.Buffer.from(b64, "base64").toString("utf8");
  }
  // Browser path: atob → binary string → TextDecoder for UTF-8.
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
}

/**
 * State derived from a verification result — what the UI should do.
 */
export type LicenseGateState =
  | { kind: "unknown" } // first launch, no cached token
  | { kind: "valid"; claims: LicenseClaims } // good to go
  | { kind: "grace"; claims: LicenseClaims; graceUntil: number } // expired but within grace
  | { kind: "blocked"; reason: VerifyResult["reason"] | (string & {}) }; // hard stop
